import React from 'react';

/**
 * Shared presentational component for the responses table used in both
 * FlowChartEditor (edge-based data) and ScriptBuilder (response-object data).
 *
 * Props:
 *  - responses: Array<{ id, label, type, target }>
 *  - targetOptions: Array<{ id, label }>  (for the "Goes To" dropdown)
 *  - onUpdateLabel(id, newLabel)
 *  - onUpdateType(id, newType)
 *  - onUpdateTarget(id, newTarget)
 *  - onRemove(id)
 *  - onAdd()
 *  - emptyMessage (optional) – text shown when there are no responses
 */
const NodeResponsesTable = ({
  responses,
  targetOptions,
  onUpdateLabel,
  onUpdateType,
  onUpdateTarget,
  onRemove,
  onAdd,
  emptyMessage = 'No responses yet. Click "+ Add Response" to create one.',
}) => {
  return (
    <div style={{
      borderTop: '1px solid #e5e7eb',
      paddingTop: '16px',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
          Responses ({responses.length})
        </label>
        <button
          type="button"
          onClick={onAdd}
          style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '5px 14px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
        >
          + Add Response
        </button>
      </div>

      {/* Empty state */}
      {responses.length === 0 ? (
        <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
          {emptyMessage}
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={styles.th('#', '28px')}>#</th>
              <th style={styles.th('Label')}>Label</th>
              <th style={styles.th('Type', '120px')}>Type</th>
              <th style={styles.th('Goes To')}>Goes To</th>
              <th style={{ width: '32px', padding: '6px 2px' }}></th>
            </tr>
          </thead>
          <tbody>
            {responses.map((resp, idx) => (
              <tr key={resp.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                {/* Row number */}
                <td style={{ padding: '5px 6px', color: '#9ca3af', fontSize: '12px' }}>{idx + 1}</td>

                {/* Label */}
                <td style={styles.cell}>
                  <input
                    type="text"
                    value={resp.label || ''}
                    onChange={(e) => onUpdateLabel(resp.id, e.target.value)}
                    style={styles.input}
                    placeholder="Response label..."
                  />
                </td>

                {/* Type */}
                <td style={styles.cell}>
                  <select
                    value={resp.type || 'predetermined'}
                    onChange={(e) => onUpdateType(resp.id, e.target.value)}
                    style={styles.select}
                  >
                    <option value="predetermined">Multiple Choice</option>
                    <option value="open_text">Open Text</option>
                  </select>
                </td>

                {/* Goes To */}
                <td style={styles.cell}>
                  <select
                    value={resp.target || ''}
                    onChange={(e) => onUpdateTarget(resp.id, e.target.value)}
                    style={styles.select}
                  >
                    {targetOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                </td>

                {/* Remove */}
                <td style={{ padding: '3px 2px', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => onRemove(resp.id)}
                    style={styles.removeBtn}
                    title="Remove response"
                    onMouseEnter={(e) => { e.target.style.background = '#fef2f2'; }}
                    onMouseLeave={(e) => { e.target.style.background = 'none'; }}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const styles = {
  th: (_label, width) => ({
    textAlign: 'left',
    padding: '6px 6px',
    color: '#6b7280',
    fontWeight: '500',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    ...(width ? { width } : {}),
  }),
  cell: {
    padding: '3px 4px',
  },
  input: {
    width: '100%',
    padding: '4px 6px',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    fontSize: '13px',
    boxSizing: 'border-box',
    background: 'white',
  },
  select: {
    width: '100%',
    padding: '4px 4px',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    fontSize: '12px',
    boxSizing: 'border-box',
    background: 'white',
  },
  removeBtn: {
    background: 'none',
    color: '#ef4444',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
    padding: '2px 4px',
    borderRadius: '4px',
    lineHeight: 1,
  },
};

export default NodeResponsesTable;
